import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';

import {
  IconAlertTriangle,
  IconCircleCheck,
  IconDownload,
  IconLoader2,
  IconPencil,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';

import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
  CheckboxField,
  Iconsvg,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tag,
} from '@wk/components-react16';

import MultiSelect from '@/shared/ui/MultiSelect';
import useToast from '@/shared/stores/toastStore';
import { getAssignableRoles } from '@/domains/access/userAccessApi';
import {
  addManagedApp,
  getManagedApps,
  removeManagedApp,
  searchApps,
  updateManagedAppRoles,
} from '@/domains/access/applicationAccessApi';
import type { AppSearchResult, ManagedApp } from '@/types';
import { downloadCsv } from '@/lib/csv';

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | { title?: string; message?: string }
    | undefined;
  return data?.title || data?.message || fallback;
};

// Azure-style read-only admin-consent indicator (built from jumpstart icons + Tag).
const ConsentBadge = ({
  granted,
  status,
}: {
  granted: boolean;
  status?: string;
}) =>
  granted ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
      <IconCircleCheck size={14} /> Granted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
      <IconAlertTriangle size={14} className="text-amber-500" />
      {status || 'Admin consent not granted'}
    </span>
  );

const allowedRoleValues = (app: ManagedApp) =>
  app.roles.filter((r) => r.pegasusAllowed).map((r) => r.roleValue);

const ApplicationsTab = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [filterTerm, setFilterTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);

  // Add-app modal: directory search.
  const [addOpen, setAddOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  // Edit-app modal: Pegasus permission matrix.
  const [editTarget, setEditTarget] = useState<ManagedApp | null>(null);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());

  const [removeTarget, setRemoveTarget] = useState<ManagedApp | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term.trim()), 350);
    return () => clearTimeout(handle);
  }, [term]);

  const appsQ = useQuery({
    queryKey: ['app-access', 'managed'],
    queryFn: async () => (await getManagedApps()).data,
  });

  const rolesQ = useQuery({
    queryKey: ['user-access', 'roles'],
    queryFn: async () => (await getAssignableRoles()).data,
    staleTime: 60 * 60 * 1000,
  });

  const searchQ = useQuery({
    queryKey: ['app-access', 'search', debounced],
    queryFn: async () => (await searchApps(debounced)).data,
    enabled: addOpen && debounced.length >= 2,
    placeholderData: keepPreviousData,
  });

  const apps = appsQ.data ?? [];
  const roleOptions = useMemo(
    () =>
      [...(rolesQ.data ?? [])]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((r) => ({ value: r.value, label: r.displayName })),
    [rolesQ.data],
  );

  const filteredApps = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    return apps.filter((app) => {
      const matchesText =
        !needle ||
        app.displayName.toLowerCase().includes(needle) ||
        app.appId.toLowerCase().includes(needle);
      const matchesRoles =
        roleFilter.length === 0 ||
        roleFilter.some((rv) =>
          app.roles.some((r) => r.roleValue === rv && r.pegasusAllowed),
        );
      return matchesText && matchesRoles;
    });
  }, [apps, filterTerm, roleFilter]);

  const addMutation = useMutation({
    mutationFn: async (app: AppSearchResult) =>
      (await addManagedApp({ appId: app.appId, name: app.displayName, roles: [] }))
        .data,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['app-access', 'managed'] });
      showToast('success', {
        title: 'Application added',
        message: `${created.displayName} added — set its Pegasus permissions.`,
      });
      setAddOpen(false);
      setTerm('');
      openEdit(created);
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not add application',
        message: apiErrorMessage(error, 'An error occurred while adding the application.'),
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ appId, roles }: { appId: string; roles: string[] }) =>
      (await updateManagedAppRoles(appId, roles)).data,
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['app-access', 'managed'] });
      showToast('success', {
        title: 'Pegasus permissions updated',
        message: `${updated.displayName} now allows ${
          allowedRoleValues(updated).length
        } role(s).`,
      });
      setEditTarget(null);
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not update permissions',
        message: apiErrorMessage(error, 'An error occurred while updating permissions.'),
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (appId: string) => removeManagedApp(appId),
    onSuccess: (_data, appId) => {
      queryClient.invalidateQueries({ queryKey: ['app-access', 'managed'] });
      const name = apps.find((a) => a.appId === appId)?.displayName ?? appId;
      showToast('success', {
        title: 'Application removed',
        message: `${name} removed from the Pegasus allowlist.`,
      });
      setRemoveTarget(null);
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not remove application',
        message: apiErrorMessage(error, 'An error occurred while removing the application.'),
      });
    },
  });

  const openEdit = (app: ManagedApp) => {
    setAllowed(new Set(allowedRoleValues(app)));
    setEditTarget(app);
  };

  const toggleAllowed = (roleValue: string) =>
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(roleValue)) next.delete(roleValue);
      else next.add(roleValue);
      return next;
    });

  const exportCsv = () => {
    const header = ['Application', 'AppId', 'Entra', 'Pegasus roles', 'Entra-granted roles'];
    const rows = filteredApps.map((app) => [
      app.displayName,
      app.appId,
      app.entraResolved ? 'Resolved' : 'Not found in Entra',
      app.roles.filter((r) => r.pegasusAllowed).map((r) => r.roleValue).join('; '),
      app.roles.filter((r) => r.entraGranted).map((r) => r.roleValue).join('; '),
    ]);
    downloadCsv(`application-access-${new Date().toISOString().slice(0, 10)}`, [
      header,
      ...rows,
    ]);
  };

  // Warn when a Pegasus-allowed role lacks Entra admin consent (allowed here, not granted in Entra).
  const appHasConsentGap = (app: ManagedApp) =>
    app.roles.some((r) => r.pegasusAllowed && !r.entraGranted);

  const searchResults = addOpen && debounced.length >= 2 ? searchQ.data ?? [] : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="cg-field flex min-w-0 flex-1 flex-col sm:max-w-xs">
          <label className="cg-field-label" htmlFor="app-filter">
            Filter
          </label>
          <div className="group border border-input text-sm hover:border-[#005b92] focus-within:border-[#000]">
            <div className="flex min-h-8 items-center gap-1 px-[15px] py-1">
              <input
                id="app-filter"
                type="text"
                value={filterTerm}
                onChange={(event) => setFilterTerm(event.target.value)}
                placeholder="Filter by name or appId"
                autoComplete="off"
                className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>
        <div className="cg-field flex w-full flex-col sm:w-64">
          <label className="cg-field-label" htmlFor="app-role-filter">
            Roles
          </label>
          <MultiSelect
            size="small"
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="Filter by Pegasus role"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-6">
          <ButtonField mode="text">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              aria-label="Add Application"
            >
              <span className="flex items-center gap-2">
                <Iconsvg name="plus" />
                <span className="max-sm:hidden">Add Application</span>
              </span>
            </button>
          </ButtonField>
          <ButtonField mode="text">
            <button
              type="button"
              onClick={exportCsv}
              aria-label="Export CSV"
              disabled={filteredApps.length === 0}
              className="disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="flex items-center gap-2">
                <IconDownload size={18} />
                <span className="max-sm:hidden">Export CSV</span>
              </span>
            </button>
          </ButtonField>
        </div>
      </div>

      <div className="cg-ag-grid-field ag-theme-balham min-h-0 flex-1">
        <AgGridReact
          animateRows={false}
          loading={appsQ.isLoading}
          rowData={filteredApps}
          columnDefs={[
            {
              headerName: 'Application',
              field: 'displayName',
              flex: 2,
              minWidth: 260,
              autoHeight: true,
              cellRenderer: (params: CustomCellRendererProps<ManagedApp>) => {
                const app = params.data;
                if (!app) return null;
                return (
                  <div className="flex flex-col py-1 leading-tight">
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{app.displayName}</span>
                      <Tag color={app.entraResolved ? 'gray' : 'orange'}>
                        {app.entraResolved ? 'Entra' : 'Not in Entra'}
                      </Tag>
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {app.appId}
                    </span>
                  </div>
                );
              },
            },
            {
              headerName: 'Pegasus roles',
              colId: 'roles',
              flex: 3,
              minWidth: 280,
              sortable: false,
              autoHeight: true,
              cellRenderer: (params: CustomCellRendererProps<ManagedApp>) => {
                const app = params.data;
                if (!app) return null;
                const allowedRoles = app.roles.filter((r) => r.pegasusAllowed);
                if (allowedRoles.length === 0)
                  return <span className="text-xs text-gray-400">None</span>;
                return (
                  <div className="flex flex-wrap items-center gap-1.5 py-2">
                    {allowedRoles.map((r) => (
                      <span
                        key={r.roleValue}
                        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs"
                      >
                        {r.roleDisplayName}
                        {!r.entraGranted ? (
                          <IconAlertTriangle
                            size={12}
                            className="text-amber-500"
                            title="Admin consent not granted"
                          />
                        ) : null}
                      </span>
                    ))}
                  </div>
                );
              },
            },
            {
              headerName: 'Entra consent',
              colId: 'consent',
              width: 200,
              sortable: false,
              cellRenderer: (params: CustomCellRendererProps<ManagedApp>) => {
                const app = params.data;
                if (!app) return null;
                // No Pegasus-allowed roles → nothing to consent; show neutral, not "Granted".
                const allowedCount = app.roles.filter((r) => r.pegasusAllowed).length;
                return (
                  <div className="flex h-full items-center">
                    {allowedCount === 0 ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <ConsentBadge granted={!appHasConsentGap(app)} />
                    )}
                  </div>
                );
              },
            },
            {
              headerName: 'Actions',
              colId: 'actions',
              width: 110,
              sortable: false,
              cellRenderer: (params: CustomCellRendererProps<ManagedApp>) => {
                const app = params.data;
                if (!app) return null;
                return (
                  <div className="flex h-full items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Edit ${app.displayName}`}
                      title="Edit Pegasus Permissions"
                      onClick={() => openEdit(app)}
                      className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-800"
                    >
                      <IconPencil size={16} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${app.displayName}`}
                      title="Remove Application"
                      onClick={() => setRemoveTarget(app)}
                      className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-red-600"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                );
              },
            },
          ]}
        />
      </div>
      <p className="text-sm text-muted-foreground">{filteredApps.length} application(s)</p>

      {/* Add application: directory search */}
      <Modal
        isOpen={addOpen}
        mobileMode="fullscreen"
        srLabelledBy="add-app-title"
        srDescribedBy="add-app-desc"
        onUserRequest={() => {
          setAddOpen(false);
          setTerm('');
        }}
      >
        <ModalHeader
          titleId="add-app-title"
          onUserRequest={() => {
            setAddOpen(false);
            setTerm('');
          }}
        >
          Add Application
        </ModalHeader>
        <ModalBody descriptionId="add-app-desc">
          <InputField label="Search enterprise apps and app registrations">
            <input
              type="text"
              name="app-search"
              placeholder="Start typing at least 2 characters…"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              autoComplete="off"
            />
          </InputField>

          {debounced.length >= 2 ? (
            searchQ.isFetching && !searchResults.length ? (
              <div className="mt-2 flex items-center gap-2 rounded-md border px-3 py-3 text-sm text-gray-500">
                <IconLoader2 size={16} className="animate-spin" /> Searching…
              </div>
            ) : searchResults.length === 0 ? (
              <div className="mt-2 flex items-center gap-2 rounded-md border px-3 py-3 text-sm text-gray-500">
                <IconSearch size={16} /> No apps match “{debounced}”.
              </div>
            ) : (
              <div className="cg-ag-grid-field ag-theme-balham mt-2 max-h-72 overflow-auto">
                <AgGridReact
                  domLayout="autoHeight"
                  headerHeight={0}
                  rowHeight={48}
                  rowData={searchResults}
                  rowStyle={{ cursor: 'pointer' }}
                  onRowClicked={(event) =>
                    event.data &&
                    !event.data.isManaged &&
                    addMutation.mutate(event.data)
                  }
                  columnDefs={[
                    {
                      headerName: 'App',
                      field: 'displayName',
                      flex: 1,
                      cellRenderer: (
                        params: CustomCellRendererProps<AppSearchResult>,
                      ) => {
                        const app = params.data;
                        if (!app) return null;
                        return (
                          <div className="flex items-center justify-between gap-2 py-1">
                            <div className="flex min-w-0 flex-col leading-tight">
                              <span className="flex items-center gap-2">
                                <span className="font-medium">
                                  {app.displayName}
                                </span>
                                <Tag
                                  color={
                                    app.source === 'ServicePrincipal'
                                      ? 'gray'
                                      : 'blue'
                                  }
                                >
                                  {app.source === 'ServicePrincipal'
                                    ? 'Enterprise app'
                                    : 'App registration'}
                                </Tag>
                              </span>
                              <span className="truncate text-xs text-gray-500">
                                {app.appId}
                              </span>
                            </div>
                            {app.isManaged ? (
                              <span className="shrink-0 text-xs text-gray-400">
                                Already added
                              </span>
                            ) : null}
                          </div>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )
          ) : null}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={() => {
                    setAddOpen(false);
                    setTerm('');
                  }}
                >
                  Close
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Edit Pegasus permissions: per-role toggle (left) + read-only Entra consent (right) */}
      <Modal
        isOpen={editTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="edit-app-title"
        srDescribedBy="edit-app-desc"
        onUserRequest={() => setEditTarget(null)}
      >
        <ModalHeader titleId="edit-app-title" onUserRequest={() => setEditTarget(null)}>
          Pegasus Permissions
        </ModalHeader>
        <ModalBody descriptionId="edit-app-desc">
          {editTarget ? (
            <>
              <div className="mb-3">
                <p className="font-medium">{editTarget.displayName}</p>
                <p className="truncate text-xs text-gray-500">{editTarget.appId}</p>
                {!editTarget.entraResolved ? (
                  <p className="mt-1">
                    <ConsentBadge
                      granted={false}
                      status="No service principal found in Entra"
                    />
                  </p>
                ) : null}
              </div>
              <div className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-gray-500">
                <span>Role — Pegasus Permission</span>
                <span>Entra (admin consent)</span>
              </div>
              <div className="max-h-80 divide-y overflow-auto rounded-md border">
                {editTarget.roles.map((role) => (
                  <div
                    key={role.roleValue}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <CheckboxField label={role.roleDisplayName}>
                      <input
                        type="checkbox"
                        name={`pegasus-${role.roleValue}`}
                        checked={allowed.has(role.roleValue)}
                        disabled={saveMutation.isPending}
                        onChange={() => toggleAllowed(role.roleValue)}
                      />
                    </CheckboxField>
                    <ConsentBadge
                      granted={role.entraGranted}
                      status={role.adminConsentStatus}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  disabled={saveMutation.isPending}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={saveMutation.isPending}
                  onClick={() =>
                    editTarget &&
                    saveMutation.mutate({
                      appId: editTarget.appId,
                      roles: [...allowed],
                    })
                  }
                >
                  Apply
                  {saveMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Remove confirmation */}
      <Modal
        isOpen={removeTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="remove-app-title"
        srDescribedBy="remove-app-desc"
        onUserRequest={() => setRemoveTarget(null)}
      >
        <ModalHeader titleId="remove-app-title" onUserRequest={() => setRemoveTarget(null)}>
          Remove Application
        </ModalHeader>
        <ModalBody descriptionId="remove-app-desc">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-500" />
            <span>
              Remove{' '}
              <span className="font-semibold">{removeTarget?.displayName}</span> from
              the Pegasus allowlist? It will lose all Pegasus view permissions
              (Entra grants are unaffected).
            </span>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={() => setRemoveTarget(null)}>
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={removeMutation.isPending}
                  onClick={() =>
                    removeTarget && removeMutation.mutate(removeTarget.appId)
                  }
                >
                  Remove
                  {removeMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApplicationsTab;
